import LegalPageLayout, { TocSection } from './LegalPageLayout';

const TOC_SECTIONS: TocSection[] = [
  { id: 'email', title: '1. Email Marketing' },
  { id: 'whatsapp', title: '2. WhatsApp Messaging' },
  { id: 'social', title: '3. Social Media' },
  { id: 'ai-usage', title: '4. AI Usage Guidelines' },
  { id: 'prohibited', title: '5. Prohibited Content' },
  { id: 'security', title: '6. Security & Infrastructure' },
  { id: 'enforcement', title: '7. Enforcement' },
  { id: 'reporting', title: '8. Reporting Violations' },
  { id: 'contact', title: '9. Contact Information' },
];

export default function AcceptableUsePolicy() {
  return (
    <LegalPageLayout
      title="Acceptable Use Policy"
      subtitle="The rules of the road for using BuzzBit X responsibly."
      effectiveDate="February 16, 2026"
      lastUpdated="February 16, 2026"
      sections={TOC_SECTIONS}
    >
      {/* 1. Email Marketing */}
      <section id="email" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">1. Email Marketing</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          All email campaigns sent through BuzzBit X must comply with CAN-SPAM, GDPR, CASL, and all other applicable anti-spam regulations. You must:
        </p>
        <ul className="list-disc ml-6 space-y-2 text-gray-600 mb-6">
          <li>Only send to recipients who have provided explicit, verifiable opt-in consent</li>
          <li>Include a clear, functional unsubscribe link in every marketing email</li>
          <li>Process unsubscribe requests within 10 business days (CAN-SPAM) or immediately where required (GDPR)</li>
          <li>Include your valid physical business address in every email</li>
          <li>Use accurate and non-deceptive sender names, &quot;From&quot; addresses, and subject lines</li>
          <li>Clearly identify the message as an advertisement where required by law</li>
        </ul>

        <h3 className="text-lg text-gray-900 font-semibold mb-3">Consent Requirements</h3>
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm leading-relaxed">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left text-gray-900 font-semibold p-3 border border-gray-200">Type</th>
                <th className="text-left text-gray-900 font-semibold p-3 border border-gray-200">Requirement</th>
                <th className="text-left text-gray-900 font-semibold p-3 border border-gray-200">Example</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-3 text-gray-600 border border-gray-200">Explicit Opt-In</td>
                <td className="p-3 text-gray-600 border border-gray-200">User actively checks a box or submits a form</td>
                <td className="p-3 text-gray-600 border border-gray-200">&quot;Yes, I want to receive marketing emails&quot;</td>
              </tr>
              <tr>
                <td className="p-3 text-gray-600 border border-gray-200">Double Opt-In</td>
                <td className="p-3 text-gray-600 border border-gray-200">Confirmation email required (recommended)</td>
                <td className="p-3 text-gray-600 border border-gray-200">Click confirmation link in welcome email</td>
              </tr>
              <tr>
                <td className="p-3 text-gray-600 border border-gray-200">Transactional</td>
                <td className="p-3 text-gray-600 border border-gray-200">Consent not required for order updates</td>
                <td className="p-3 text-gray-600 border border-gray-200">Order confirmations, shipping notifications</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-gray-600 leading-relaxed">
          <strong className="text-gray-900">Purchased lists are strictly prohibited.</strong> You may not import or use email lists that were purchased, rented, or scraped from public sources. All contacts must have been collected through your own legitimate business activities.
        </p>
      </section>

      {/* 2. WhatsApp Messaging */}
      <section id="whatsapp" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">2. WhatsApp Messaging</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          WhatsApp messaging through BuzzBit X is governed by Meta&apos;s WhatsApp Business Policy and Commerce Policy in addition to these terms:
        </p>
        <ul className="list-disc ml-6 space-y-2 text-gray-600 mb-4">
          <li><strong className="text-gray-900">24-Hour Window:</strong> Free-form messages may only be sent within 24 hours of the customer&apos;s last message to you</li>
          <li><strong className="text-gray-900">Template Messages:</strong> Messages sent outside the 24-hour window must use pre-approved WhatsApp message templates</li>
          <li><strong className="text-gray-900">Opt-In Required:</strong> You must obtain explicit opt-in consent before initiating WhatsApp conversations</li>
          <li><strong className="text-gray-900">Business Verification:</strong> Your WhatsApp Business Account must be verified and in good standing</li>
          <li><strong className="text-gray-900">Message Quality:</strong> Maintain a high-quality rating. Excessive blocks or reports may result in account restrictions</li>
        </ul>
        <p className="text-gray-600 leading-relaxed">
          BuzzBit X monitors your WhatsApp quality rating and will notify you if it drops below acceptable thresholds. Repeated quality issues may result in temporary suspension of WhatsApp features.
        </p>
      </section>

      {/* 3. Social Media */}
      <section id="social" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">3. Social Media</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          When using BuzzBit X to manage social media accounts, you must comply with each platform&apos;s terms of service, community guidelines, and advertising policies:
        </p>
        <ul className="list-disc ml-6 space-y-2 text-gray-600 mb-4">
          <li>Only connect social media accounts that you own or have explicit authorization to manage</li>
          <li>Do not use automated tools to artificially inflate engagement (likes, follows, comments)</li>
          <li>Disclose sponsored content and paid partnerships as required by FTC guidelines and local regulations</li>
          <li>Do not post content that violates platform-specific community guidelines</li>
          <li>Respect rate limits and API usage guidelines for each connected platform</li>
          <li>Maintain authentic engagement and do not engage in coordinated inauthentic behavior</li>
        </ul>
        <p className="text-gray-600 leading-relaxed">
          BuzzBit X is not responsible for actions taken by social media platforms against your accounts as a result of your content or posting behavior.
        </p>
      </section>

      {/* 4. AI Usage Guidelines */}
      <section id="ai-usage" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">4. AI Usage Guidelines</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          BuzzBit X&apos;s AI features are powerful tools that require responsible use. When using AI-generated content:
        </p>
        <ul className="list-disc ml-6 space-y-2 text-gray-600 mb-4">
          <li><strong className="text-gray-900">Review Before Publishing:</strong> Always review and edit AI-generated content before sending it to customers. You are responsible for all published content.</li>
          <li><strong className="text-gray-900">No Misrepresentation:</strong> Do not present AI-generated content as human-written testimonials, reviews, or endorsements</li>
          <li><strong className="text-gray-900">Factual Accuracy:</strong> Verify all claims, statistics, and product details in AI-generated content before publishing</li>
          <li><strong className="text-gray-900">Disclosure:</strong> Where required by law or platform policy, disclose the use of AI-generated content</li>
          <li><strong className="text-gray-900">No Manipulation:</strong> Do not use AI tools to generate content designed to manipulate, deceive, or exploit vulnerable populations</li>
          <li><strong className="text-gray-900">Data Privacy:</strong> Do not input sensitive personal data (SSN, health records, financial details) into AI content generation prompts</li>
        </ul>
        <p className="text-gray-600 leading-relaxed">
          BuzzBit X reserves the right to disable AI features for accounts that repeatedly misuse AI-generated content or violate these guidelines.
        </p>
      </section>

      {/* 5. Prohibited Content */}
      <section id="prohibited" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">5. Prohibited Content</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          The following types of content are strictly prohibited on BuzzBit X. Sending prohibited content will result in immediate account suspension:
        </p>
        <ul className="list-disc ml-6 space-y-2 text-gray-600 mb-4">
          <li>Adult, sexually explicit, or pornographic content</li>
          <li>Illegal drugs, controlled substances, or drug paraphernalia</li>
          <li>Weapons, ammunition, or explosives</li>
          <li>Gambling services (unless properly licensed in all recipient jurisdictions)</li>
          <li>Counterfeit goods or intellectual property infringement</li>
          <li>Phishing, social engineering, or identity theft schemes</li>
          <li>Pyramid schemes, multi-level marketing scams, or Ponzi schemes</li>
          <li>Hate speech, discrimination, or content promoting violence</li>
          <li>Harassment, threats, bullying, or intimidation</li>
          <li>Malware, ransomware, spyware, or other malicious software</li>
          <li>False or misleading health claims or miracle cures</li>
          <li>Deceptive financial products or get-rich-quick schemes</li>
          <li>Content that exploits or endangers minors in any way</li>
        </ul>
        <p className="text-gray-600 leading-relaxed">
          If you are unsure whether your content is permitted, please contact our compliance team at{' '}
          <a href="mailto:compliance@buzzbitx.com" className="text-teal-400 hover:text-teal-300 underline">compliance@buzzbitx.com</a>{' '}
          before sending.
        </p>
      </section>

      {/* 6. Security & Infrastructure */}
      <section id="security" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">6. Security &amp; Infrastructure</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          You must not engage in any activity that compromises the security, integrity, or availability of BuzzBit X:
        </p>
        <ul className="list-disc ml-6 space-y-2 text-gray-600 mb-4">
          <li>Do not attempt to access accounts, data, or systems that you are not authorized to access</li>
          <li>Do not attempt to probe, scan, or test the vulnerability of our systems without prior written authorization</li>
          <li>Do not attempt to reverse-engineer, decompile, or disassemble any part of the Platform</li>
          <li>Do not use automated tools (bots, scrapers, crawlers) to access the Platform unless authorized via our API</li>
          <li>Do not attempt to circumvent rate limits, access controls, or security measures</li>
          <li>Do not interfere with or disrupt the Platform or its associated servers and networks</li>
          <li>Do not use the Platform to launch attacks against any third-party systems</li>
        </ul>
        <p className="text-gray-600 leading-relaxed">
          If you discover a security vulnerability, please report it responsibly to{' '}
          <a href="mailto:security@buzzbitx.com" className="text-teal-400 hover:text-teal-300 underline">security@buzzbitx.com</a>.
          We appreciate responsible disclosure and will not take legal action against good-faith security researchers.
        </p>
      </section>

      {/* 7. Enforcement */}
      <section id="enforcement" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">7. Enforcement</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          We take violations of this Acceptable Use Policy seriously. Enforcement actions are proportional to the severity and frequency of the violation:
        </p>

        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm leading-relaxed">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left text-gray-900 font-semibold p-3 border border-gray-200">Severity</th>
                <th className="text-left text-gray-900 font-semibold p-3 border border-gray-200">Action</th>
                <th className="text-left text-gray-900 font-semibold p-3 border border-gray-200">Example</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-3 text-gray-600 border border-gray-200">First Violation (Minor)</td>
                <td className="p-3 text-gray-600 border border-gray-200">Written warning + mandatory compliance review</td>
                <td className="p-3 text-gray-600 border border-gray-200">Missing unsubscribe link, incorrect sender info</td>
              </tr>
              <tr>
                <td className="p-3 text-gray-600 border border-gray-200">Second Violation</td>
                <td className="p-3 text-gray-600 border border-gray-200">30-day feature restriction + campaign review</td>
                <td className="p-3 text-gray-600 border border-gray-200">Repeated spam complaints, quiet hour violations</td>
              </tr>
              <tr>
                <td className="p-3 text-gray-600 border border-gray-200">Third Violation</td>
                <td className="p-3 text-gray-600 border border-gray-200">60-day account suspension</td>
                <td className="p-3 text-gray-600 border border-gray-200">Pattern of non-compliance after prior warnings</td>
              </tr>
              <tr>
                <td className="p-3 text-gray-600 border border-gray-200">Severe Violation</td>
                <td className="p-3 text-gray-600 border border-gray-200">Immediate permanent termination + legal referral</td>
                <td className="p-3 text-gray-600 border border-gray-200">Phishing, malware distribution, illegal content</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-gray-600 leading-relaxed">
          We reserve the right to skip warning stages and immediately terminate accounts that pose a severe risk to our platform, our users, or the public. Terminated accounts are not eligible for refunds.
        </p>
      </section>

      {/* 8. Reporting Violations */}
      <section id="reporting" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">8. Reporting Violations</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          If you receive unwanted messages sent through BuzzBit X, or if you believe a BuzzBit X user is violating this Acceptable Use Policy, please report it:
        </p>
        <ul className="list-disc ml-6 space-y-2 text-gray-600 mb-4">
          <li><strong className="text-gray-900">Email:</strong>{' '}
            <a href="mailto:abuse@buzzbitx.com" className="text-teal-400 hover:text-teal-300 underline">abuse@buzzbitx.com</a>
          </li>
          <li><strong className="text-gray-900">Include:</strong> The sender&apos;s name or email address, a copy of the message, and a description of the violation</li>
          <li><strong className="text-gray-900">Response Time:</strong> We investigate all reports within 2 business days and will notify you of the outcome</li>
        </ul>
        <p className="text-gray-600 leading-relaxed">
          We take all abuse reports seriously and will take appropriate action in accordance with our enforcement policy. Reporters&apos; identities are kept confidential.
        </p>
      </section>

      {/* 9. Contact Information */}
      <section id="contact" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">9. Contact Information</h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          For questions about this Acceptable Use Policy or to discuss compliance requirements:
        </p>
        <div className="bg-gray-50/50 border border-gray-200 rounded-lg p-6 space-y-3">
          <p className="text-gray-600">
            <strong className="text-gray-900">BuzzBit X Ltd.</strong>
          </p>
          <p className="text-gray-600">
            <strong className="text-gray-900">Compliance Team:</strong>{' '}
            <a href="mailto:compliance@buzzbitx.com" className="text-teal-400 hover:text-teal-300 underline">compliance@buzzbitx.com</a>
          </p>
          <p className="text-gray-600">
            <strong className="text-gray-900">Abuse Reports:</strong>{' '}
            <a href="mailto:abuse@buzzbitx.com" className="text-teal-400 hover:text-teal-300 underline">abuse@buzzbitx.com</a>
          </p>
          <p className="text-gray-600">
            <strong className="text-gray-900">Security Issues:</strong>{' '}
            <a href="mailto:security@buzzbitx.com" className="text-teal-400 hover:text-teal-300 underline">security@buzzbitx.com</a>
          </p>
          <p className="text-gray-600">
            <strong className="text-gray-900">General Support:</strong>{' '}
            <a href="mailto:support@buzzbitx.com" className="text-teal-400 hover:text-teal-300 underline">support@buzzbitx.com</a>
          </p>
        </div>
        <p className="text-gray-600 leading-relaxed mt-4">
          This policy should be read in conjunction with our{' '}
          <a href="/terms" className="text-teal-400 hover:text-teal-300 underline">Terms of Service</a>{' '}
          and{' '}
          <a href="/privacy" className="text-teal-400 hover:text-teal-300 underline">Privacy Policy</a>.
        </p>
      </section>
    </LegalPageLayout>
  );
}
